import { forwardRef, memo, ReactNode, useEffect, useMemo, useState } from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  Box,
  Grid,
  Input,
  Loader,
  MantineSize,
  Select,
  ComboboxItem,
  useMantineColorScheme,
  TextInput
} from '@mantine/core';
import { TextInputProps } from '@ui/inputs';

import {
  GroupSelectOverrideClassNames,
  GroupInputOverrideClassNames
} from '../groupstyles';

interface TextSelectGroupProps extends Omit<TextInputProps, 'onChange'> {
  /**
   * enable loading
   */
  loading?: boolean;

  label?: ReactNode;

  description?: string;
  /**
   * text input value
   */
  value?: string;
  /**
   * Select value
   */
  selectValue?: string | null;
  /**
   * Select options
   */
  selectData: ComboboxItem[];

  textPlaceholder?: string;
  /**
   * Placeholder for select field
   */
  selectPlaceholder?: string;
  /**
   * Icon for text field
   */
  textIcon?: ReactNode;
  /**
   * icon for select field
   */
  selectIcon?: ReactNode;
  disabled?: boolean;
  size?: MantineSize;
  error?: string;
  selectError?: string;
  onChange?: (value: string) => void;
  onChangeSelectVal?: (val: string) => void;
  textSpan?: number;
  selectSpan?: number;
}

const TextSelectGroup = memo(
  forwardRef<HTMLInputElement, TextSelectGroupProps>(
    (
      {
        size = 'md',
        loading = false,
        label,
        description,
        value,
        selectValue,
        selectData,
        selectPlaceholder,
        selectIcon,
        disabled,
        error = '',
        selectError = '',
        textPlaceholder,
        textIcon,
        textSpan = 7,
        selectSpan = 5,
        onChange,
        onChangeSelectVal,
        name,
        ...props
      },
      ref
    ) => {
      const scheme = useMantineColorScheme();
      const [inputFocussed, setInputFocussed] = useState<boolean>(false);
      const [selectFocussed, setSelectFocussed] = useState<boolean>(false);
      const [localSelect, setLocalSelect] = useState<string>(
        selectValue ? selectValue : ''
      );
      const [localValue, setLocalValue] = useState<string>(value ?? '');
      const [localError, setError] = useState<string>();

      useEffect(() => {
        setLocalValue(value ?? '');
      }, [value]);

      useEffect(() => {
        if (error !== localError || selectError !== localError) {
          setError(error || selectError);
        }
      }, [error, selectError]);

      useEffect(() => {
        setLocalSelect(selectValue ? selectValue : '');
      }, [selectValue]);

      const rightSectionSelect = loading ? (
        <Loader size={size} />
      ) : error ? (
        <RiErrorWarningLine />
      ) : (
        <RiArrowDownSLine />
      );

      const inputClasses = useMemo(() => {
        return GroupInputOverrideClassNames({
          size,
          focussed: inputFocussed,
          error,
          pos: 'right'
        });
      }, [size, inputFocussed, error]);

      const selectClasses = useMemo(() => {
        return GroupSelectOverrideClassNames({
          size,
          focussed: selectFocussed,
          error,
          pos: 'left'
        });
      }, [size, selectFocussed, error]);

      const handleChange = (val: string, selectVal: string) => {
        setLocalValue(val);
        setLocalSelect(selectVal);
        onChange?.(val);
        onChangeSelectVal?.(selectVal);
      };

      return (
        <Box lh={1.55}>
          <Grid gutter={0}>
            {(label || description) && (
              <Grid.Col span={12} mb={4}>
                {label && (
                  <Input.Label
                    fw={500}
                    styles={(theme) => ({
                      label: {
                        color:
                          scheme.colorScheme === 'light'
                            ? theme.colors.text[6]
                            : undefined
                      }
                    })}
                    size={size}
                  >
                    {label}
                  </Input.Label>
                )}
                {description && (
                  <Input.Description size={size}>{description}</Input.Description>
                )}
              </Grid.Col>
            )}

            <Grid.Col span={selectSpan} my={2}>
              <Select
                onFocus={() => setSelectFocussed(true)}
                onBlur={() => setSelectFocussed(false)}
                placeholder={selectPlaceholder}
                leftSection={selectIcon}
                data={selectData}
                size={size}
                rightSection={rightSectionSelect}
                disabled={disabled}
                value={localSelect}
                onChange={(e) =>
                  e !== null && handleChange(localValue ? localValue : '', e)
                }
                classNames={{ ...selectClasses }}
                error={error ? <></> : undefined}
              />
            </Grid.Col>
            <Grid.Col span={textSpan} my={2}>
              <TextInput
                name={name}
                onFocus={() => setInputFocussed(true)}
                onBlur={() => setInputFocussed(false)}
                placeholder={textPlaceholder}
                leftSection={undefined}
                rightSection={textIcon}
                size={size}
                disabled={disabled}
                classNames={{ ...inputClasses }}
                value={localValue ? localValue : ''}
                onChange={(e) => handleChange(e ? e.target.value : '', localSelect)}
                ref={ref}
                error={error ? <></> : undefined}
                {...props}
              />
            </Grid.Col>
            {localError && (
              <Grid.Col span={12} mt={4}>
                <Input.Error c="pink.6" size={size}>
                  {localError}
                </Input.Error>
              </Grid.Col>
            )}
          </Grid>
        </Box>
      );
    }
  )
);

export { TextSelectGroup };
export type { TextSelectGroupProps };
